import {
  useListProducts,
  useListCategories,
  useListCustomers,
  useGetStore,
  customFetch,
  getListOrdersQueryKey,
} from "@workspace/api-client-react";
import { sentenceCasePtBR } from "@/lib/textNormalize";
import type { Product, Category, Customer } from "@workspace/api-client-react";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { formatCurrency, orderDisplayCode, temWhatsApp } from "@/lib/utils";
import { quantityPromoDiscounts } from "@/lib/promotion";
import { normalizarCodigoProduto, limparCodigoProduto, formatarCodigoProduto } from "@workspace/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsUltra, useFeatureAllowed } from "@/hooks/use-plan";
import { UltraBadge } from "@/components/UltraBadge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";
import { useAuth } from "@/hooks/use-auth";
import { PublicLinkCard } from "@/components/PublicLinkCard";
import { usePdvCart } from "@/hooks/use-pdv-cart";
import { PontosDoPedido } from "@/components/catalog/PontosDoPedido";
import { EditCustomerModal } from "@/components/admin/EditCustomerModal";
import {
  ProductCard, CartItemRow, CustomerSection, DeliverySection,
  DiscountSection, PaymentSection, ProductDetailDialog, SuccessDialog,
  SuggestiveSection, MinOrderBanner, MesaSection, NotifyCustomerDialog, productHasOptions, EncomendaDoCarrinhoPDV, getPaymentMethodLabel,
  AVISO_PONTOS_SEM_WHATSAPP } from "@/components/pdv/PDVShared";
import { ModalShell } from "@/components/ui/modal-shell";
import { extractApiErrorMessage } from "@/lib/api-error";
import { computeCardSurcharge } from "@/lib/use-delivery-fee";
import { itensDeEncomenda } from "@/lib/encomendaUnica";
import { groupHasWeights, semPesos } from "@workspace/pricing";
import {
  Search,
  ShoppingCart,
  X,
  Package,
  CheckCircle2,
  Calculator,
  Loader2,
  Printer,
  UtensilsCrossed,
  MapPin,
  Pencil,
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Plus,
  Barcode,
  CornerDownLeft,
  ScanQrCode,
} from "lucide-react";
import { LerQrDaMesa } from "@/components/pdv/LerQrDaMesa";
import { NomeDaMesa, nomeDaMesa } from "@/lib/mesa";

// Comandas abertas do PDV. Antes era uma FAIXA horizontal de pílulas: com 8
// contas o conteúdo media 2312px numa faixa de 326px no celular (medido em
// 05/09/2026), ou seja 86% escondido, sem contador que dissesse quantas eram —
// e ficava colada nas pílulas de CATEGORIA, mesma forma pra significado oposto.
// Virou botão com contador que abre uma lista, desenho que a sessão irmã do
// VetZap já tinha validado com o Pedro. O que é nosso e não é de lá: o TEMPO
// aberto em destaque, porque numa mesa é o dado que mais importa, e a soma do
// que está em aberto no próprio botão.
export function tempoAberto(iso: string): { texto: string; velha: boolean } {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return { texto: `há ${min} min`, velha: false };
  const h = Math.floor(min / 60);
  if (h < 24) return { texto: `há ${h}h${String(min % 60).padStart(2, "0")}`, velha: h >= 12 };
  const d = Math.floor(h / 24);
  return { texto: d === 1 ? "há 1 dia" : `há ${d} dias`, velha: true };
}

// O campo é texto livre: vem "5", mas também "Mesa do fundo", "Comanda 12" ou
// o nome do cliente. Prefixar "Mesa" sempre produzia "Mesa Mesa do fundo".
// Com a comanda individual («1 QR code para cada cliente») o número é de uma comanda: «Comanda 12».
function rotuloConta(tableNumber: string | null, porCliente = false): string {
  const t = String(tableNumber ?? "").trim();
  if (!t) return "Balcão";
  return /^\d+$/.test(t) ? `${NomeDaMesa(porCliente)} ${t}` : t;
}

type OpenTab = {
  id: string;
  tableNumber: string | null;
  dailyOrderNumber: number | null;
  total: string;
  status: string;
  paymentMethod: string;
  createdAt: string;
  customerName: string | null;
  customerId?: string | null;
  customerWhatsapp?: string | null;
  itemCount: number;
  firstItem: string | null;
  source?: string;
  deliveryFee?: string | null;
  deliveryMode?: string | null;
};

// «1 QR code para cada cliente» (Pedro, 29/10/2026): a mesa presa a um cliente, com ou sem pedido.
type MesaPresa = {
  mesa: string;
  customerId: string;
  nome: string | null;
  whatsapp: string | null;
  whatsappNumber?: string | null;
  desde: string;
  /** Cadastrada no PDV e ainda sem o celular do cliente. */
  esperando?: boolean;
  /** A mesa onde o cliente está (ele diz na primeira leitura do QR). */
  local?: string | null;
};

// "Mesa 5", "mesa 5" e "5" são a mesma mesa (mesma regra do servidor).
function mesaKey(tableNumber: string | null): string {
  return String(tableNumber ?? "").trim().toLowerCase().replace(/^mesa\s*/, "");
}

// Contas da mesma mesa juntas (06/10/2026): com o pedido na mesa pelo QR Code
// cada rodada é um pedido, e a mesa é a soma deles. Conta sem mesa fica sozinha.
// Com o «1 QR code para cada cliente» (29/10/2026), a conta é do cliente que
// segura a mesa: o rótulo é o WhatsApp dele e o botão, «Fechar conta».
type ClienteDaConta = { id: string; nome: string | null; whatsapp: string | null; mesa: string; esperando?: boolean; local?: string | null };
type GrupoConta = { key: string; tabs: OpenTab[]; cliente?: ClienteDaConta };

function agruparContas(lista: OpenTab[], porCliente: boolean, presas: MesaPresa[]): GrupoConta[] {
  const grupos: GrupoConta[] = [];
  const porChave = new Map<string, GrupoConta>();
  // O pedido que o PDV lançou na comanda (o «+» de dentro dela) entra na conta de quem segura a comanda.
  const donoDaComanda = new Map(presas.map((p) => [mesaKey(p.mesa), p.customerId]));
  const esperando = new Set(presas.filter((p) => p.esperando).map((p) => `${mesaKey(p.mesa)}:${p.customerId}`));
  const localDe = new Map(presas.map((p) => [`${mesaKey(p.mesa)}:${p.customerId}`, p.local ?? null]));
  for (const t of lista) {
    const k = mesaKey(t.tableNumber);
    if (!k) {
      grupos.push({ key: `id:${t.id}`, tabs: [t] });
      continue;
    }
    const doCliente = porCliente && !!t.customerId && (t.source === "link" || donoDaComanda.get(k) === t.customerId);
    const key = doCliente ? `cliente:${k}:${t.customerId}` : `mesa:${k}`;
    const g = porChave.get(key);
    if (g) g.tabs.push(t);
    else {
      const novo: GrupoConta = doCliente
        ? {
            key,
            tabs: [t],
            cliente: {
              id: t.customerId!,
              nome: t.customerName,
              whatsapp: t.customerWhatsapp ?? null,
              mesa: k,
              esperando: esperando.has(`${k}:${t.customerId}`),
              local: localDe.get(`${k}:${t.customerId}`) ?? null,
            },
          }
        : { key, tabs: [t] };
      porChave.set(key, novo);
      grupos.push(novo);
    }
  }
  // Quem segura a mesa e ainda não pediu também tem conta: «Fechar conta» solta o QR.
  if (porCliente) {
    for (const p of presas) {
      const k = mesaKey(p.mesa);
      const key = `cliente:${k}:${p.customerId}`;
      if (porChave.has(key)) continue;
      grupos.push({ key, tabs: [], cliente: { id: p.customerId, nome: p.nome, whatsapp: p.whatsapp, mesa: k, esperando: p.esperando, local: p.local } });
    }
  }
  return grupos;
}

// Mesa compartilhada (Pedro, 29/10/2026): os pedidos e o total separados por cliente; tocar no cliente fecha só a
// conta dele, e «Fechar mesa» continua fechando a mesa toda.
type ClienteDaMesa = { key: string; nome: string; whatsapp: string | null; tabs: OpenTab[] };

function clientesDaMesa(tabs: OpenTab[]): ClienteDaMesa[] {
  const m = new Map<string, ClienteDaMesa>();
  for (const t of tabs) {
    const key = t.customerId ?? "sem";
    const c = m.get(key);
    if (c) c.tabs.push(t);
    else m.set(key, { key, nome: t.customerName || "Cliente", whatsapp: t.customerWhatsapp ?? null, tabs: [t] });
  }
  return [...m.values()];
}

function rotuloDoGrupo(g: GrupoConta, porCliente: boolean): string {
  if (g.cliente) return g.cliente.whatsapp || g.cliente.nome || "Cliente";
  return rotuloConta(g.tabs[0]?.tableNumber ?? null, porCliente);
}

// Lupa das Comandas abertas (Pedro, 29/10/2026): filtra a partir do 3º caractere, pelo telefone ou pelo nome.
const semAcento = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function grupoBate(g: GrupoConta, busca: string): boolean {
  const q = busca.trim();
  if (q.length < 3) return true;
  const digitos = q.replace(/\D/g, "");
  const texto = semAcento(q);
  const pessoas = [
    ...(g.cliente ? [{ nome: g.cliente.nome, whatsapp: g.cliente.whatsapp }] : []),
    // O campo da mesa no PDV é texto livre: às vezes é o nome do cliente.
    ...g.tabs.flatMap((t) => [{ nome: t.customerName, whatsapp: t.customerWhatsapp ?? null }, { nome: t.tableNumber, whatsapp: null }]),
  ];
  return pessoas.some(
    (p) =>
      (digitos.length >= 3 && String(p.whatsapp ?? "").replace(/\D/g, "").includes(digitos)) ||
      (!!p.nome && semAcento(p.nome).includes(texto)),
  );
}

// Conta paga só sai de Comandas abertas no Entregue (19/10/2026). O mesmo PUT do «Alterar sem Enviar» do Pedidos:
// receita, pontos, custo e histórico, sem mensagem ao cliente.
function marcarComoEntregue(id: string) {
  return customFetch(`/api/orders/${id}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "entregue", notifyCustomer: false }),
  });
}

// Loja que nunca conectou impressora: a conta sai na janela de impressão do navegador.
export function imprimirNaJanela(texto: string): boolean {
  const win = window.open("", "_blank", "width=420,height=640");
  if (!win) return false;
  const seguro = texto.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Conta</title><style>body{margin:8px}pre{font:13px/1.35 ui-monospace,Menlo,Consolas,monospace;white-space:pre}@media print{body{margin:0}}</style></head><body><pre>${seguro}</pre><script>setTimeout(()=>{window.print();window.close()},300);<\/script></body></html>`,
  );
  win.document.close();
  return true;
}

const buscarContas = () => customFetch<OpenTab[]>("/api/orders/open-tabs");
const buscarMesasPresas = () => customFetch<{ porCliente: boolean; contas: MesaPresa[] }>("/api/orders/open-tabs/contas");

type Fechamento = { grupo: GrupoConta; titulo: string; subtitulo?: string; feito: string };

function OpenTabsPicker({
  onPick,
  editingId,
  codeStyle,
  acceptedPayments,
  cardSurchargeConfig,
  porCliente,
  onPedidoNaComanda,
  soBalcao = false,
  taxaServico,
}: {
  onPick: (o: any) => void | Promise<void>;
  editingId: string | null;
  codeStyle?: string | null;
  acceptedPayments: string[];
  cardSurchargeConfig?: unknown;
  /** «1 QR code para cada cliente» ligado na loja. */
  porCliente: boolean;
  /** O «+» de dentro da comanda: pedido novo no PDV, já com o cliente e a comanda. */
  onPedidoNaComanda: (c: { mesa: string; cliente: Customer }) => void;
  /** Ultra com a página Comandas/Mesas: aqui ficam só as contas de balcão (sem número). */
  soBalcao?: boolean;
  /** Taxa de serviço da loja (Configurações → PDV): o «Fechar conta» já vem com ela ligada. */
  taxaServico?: { ativo?: boolean; percentual?: number | string } | null;
}) {
  const [aberto, setAberto] = useState(false);
  // A linha da lista é um RESUMO (sem itens); quem abre precisa buscar o
  // pedido inteiro, então o clique é assíncrono e a lista só fecha depois.
  const [carregando, setCarregando] = useState<string | null>(null);
  const [fechando, setFechando] = useState<Fechamento | null>(null);
  const [soltando, setSoltando] = useState<GrupoConta | null>(null);
  const [entregando, setEntregando] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [busca, setBusca] = useState("");
  const [lendoQr, setLendoQr] = useState(false);
  const [imprimindo, setImprimindo] = useState<string | null>(null);
  // O «+» da barra (e o «Ler QR code» de comanda livre): cadastrar o cliente numa comanda.
  const [cadastro, setCadastro] = useState<{ mesa?: string } | null>(null);
  // Comanda individual: os cartões abertos mostram os pedidos da comanda.
  const [expandidas, setExpandidas] = useState<Set<string>>(() => new Set());
  const qc = useQueryClient();
  const { toast } = useToast();
  const entregar = async (t: OpenTab) => {
    setEntregando(t.id);
    try {
      await marcarComoEntregue(t.id);
      toast({ title: `Pedido #${orderDisplayCode(t, codeStyle)} entregue` });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível marcar como entregue."), variant: "destructive" });
    } finally {
      setEntregando(null);
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
      qc.invalidateQueries({ queryKey: getListOrdersQueryKey() });
    }
  };
  const { data: tabs = [] } = useQuery<OpenTab[]>({
    queryKey: ["pdv-open-tabs"],
    queryFn: buscarContas,
    refetchInterval: 30_000,
  });
  const { data: presas } = useQuery({
    queryKey: ["pdv-open-tabs-contas"],
    queryFn: buscarMesasPresas,
    enabled: porCliente,
    refetchInterval: 30_000,
  });
  // Mais VELHA primeiro: é a que precisa de atenção. O servidor devolve as
  // mais recentes (o teto de 60 não pode cortar a conta de agora).
  const lista = useMemo(
    () => [...tabs].filter((t) => !soBalcao || !mesaKey(t.tableNumber)).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)),
    [tabs, soBalcao],
  );
  const grupos = useMemo(() => agruparContas(lista, porCliente, presas?.contas ?? []), [lista, porCliente, presas]);
  const visiveis = useMemo(() => grupos.filter((g) => grupoBate(g, busca)), [grupos, busca]);
  const semPedido = grupos.filter((g) => g.tabs.length === 0).length;
  // Com a comanda individual a barra fica sempre: o «+» cadastra o cliente antes do primeiro pedido.
  if (lista.length === 0 && semPedido === 0 && !porCliente) return null;
  const soma = lista.reduce((s, t) => s + parseFloat(t.total || "0"), 0);
  // Conta parada há mais de 12h quase sempre é conta esquecida — o lojista
  // precisa ver isso SEM abrir a lista.
  const atrasadas = lista.filter((t) => tempoAberto(t.createdAt).velha).length;
  // A mesa (ou o WhatsApp) aparece também com a lista fechada (Pedro, 29/10/2026). «Balcão» sozinho não diz nada.
  const rotulos = grupos.map((g) => rotuloDoGrupo(g, porCliente)).filter((r) => r !== "Balcão");
  const buscaAtiva = busca.trim().length >= 3;
  const mostrarLista = aberto || buscaAtiva;

  const abrirFechamento = (g: GrupoConta, soDoCliente?: ClienteDaMesa) => {
    if (g.cliente && g.tabs.length === 0) {
      setSoltando(g);
      return;
    }
    const alvo = (soDoCliente?.tabs ?? g.tabs).filter((t) => t.paymentMethod === "Pendente");
    if (alvo.length === 0) {
      setAberto(true);
      toast({ title: "Esta conta já está paga. Marque os pedidos como entregues na lista." });
      return;
    }
    if (g.cliente) {
      const c = g.cliente;
      setFechando({
        grupo: { ...g, tabs: alvo },
        titulo: "Fechar conta",
        subtitulo: [c.whatsapp, c.nome, `${NomeDaMesa(porCliente)} ${c.mesa}`].filter(Boolean).join(" · "),
        feito: "Conta fechada",
      });
    } else if (soDoCliente) {
      setFechando({
        grupo: { key: g.key, tabs: alvo },
        titulo: "Fechar conta",
        subtitulo: [soDoCliente.nome, soDoCliente.whatsapp, rotuloConta(g.tabs[0].tableNumber, porCliente)].filter(Boolean).join(" · "),
        feito: `Conta de ${soDoCliente.nome} fechada`,
      });
    } else {
      const r = rotuloConta(g.tabs[0].tableNumber, porCliente);
      setFechando({ grupo: { key: g.key, tabs: alvo }, titulo: `Fechar ${/^(mesa|comanda) /i.test(r) ? r.toLowerCase() : r}`, feito: `${r} fechada` });
    }
  };

  // «Ler QR code» (Pedro, 29/10/2026): o servidor confere que o QR é de uma mesa desta loja e a conta abre para
  // fechar — a do cliente que segura a mesa, ou a mesa toda. A lista vem de novo: a rodada de agora entra.
  const lerQr = async (texto: string): Promise<string | null> => {
    let mesa: string;
    try {
      ({ mesa } = await customFetch<{ mesa: string }>("/api/orders/open-tabs/ler-qr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texto }),
      }));
    } catch (err) {
      return extractApiErrorMessage(err, "Não foi possível ler este QR code.");
    }
    setLendoQr(false);
    try {
      const [frescas, frescasPresas] = await Promise.all([
        qc.fetchQuery({ queryKey: ["pdv-open-tabs"], queryFn: buscarContas, staleTime: 0 }),
        porCliente ? qc.fetchQuery({ queryKey: ["pdv-open-tabs-contas"], queryFn: buscarMesasPresas, staleTime: 0 }) : null,
      ]);
      const ordenadas = [...frescas].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
      const agora = agruparContas(ordenadas, porCliente, frescasPresas?.contas ?? []);
      const k = mesaKey(mesa);
      const g = agora.find((x) => x.cliente?.mesa === k) ?? agora.find((x) => x.key === `mesa:${k}`);
      // Comanda sem cliente: o cadastro do cliente nela (Pedro, 29/10/2026).
      if (!g && porCliente) setCadastro({ mesa });
      else if (!g) toast({ title: `A ${nomeDaMesa(porCliente)} ${mesa} não tem conta aberta.` });
      else abrirFechamento(g);
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível abrir as contas. Tente de novo."), variant: "destructive" });
    }
    return null;
  };

  // nivel 0: conta sozinha; 1: pedido dentro da mesa ou da conta; 2: pedido dentro do cliente da mesa.
  const linha = (t: OpenTab, nivel: 0 | 1 | 2, titulo?: string) => {
    const t1 = tempoAberto(t.createdAt);
    const editando = t.id === editingId;
    const detalhe = [
      t.itemCount > 0 ? `${t.itemCount} ${t.itemCount === 1 ? "item" : "itens"}` : null,
      t.firstItem,
    ].filter(Boolean).join(" · ");
    const pago = t.paymentMethod !== "Pendente";
    const recuo = nivel === 0 ? "px-3" : nivel === 1 ? "pl-7 pr-3" : "pl-10 pr-3";
    return (
      <li key={t.id} className={editando ? "bg-amber-100 dark:bg-amber-900/40" : undefined}>
        <button
          type="button"
          disabled={editando || carregando !== null}
          onClick={async () => {
            setCarregando(t.id);
            try {
              await onPick(t);
              setAberto(false);
            } finally {
              setCarregando(null);
            }
          }}
          className={`w-full flex items-center gap-3 ${recuo} py-2.5 text-left transition-colors ${editando ? "bg-amber-100 dark:bg-amber-900/40 cursor-default" : "hover:bg-amber-100/70 dark:hover:bg-amber-900/30"}`}
          data-testid={`pdv-open-tab-${t.id}`}
        >
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="font-semibold text-sm truncate">
                {titulo ?? (nivel > 0 ? (t.customerName || "Pedido") : rotuloConta(t.tableNumber, porCliente))}
              </span>
              <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                #{orderDisplayCode(t, codeStyle)}
              </span>
              {t.source === "link" && (
                <span className="text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300 shrink-0">QR</span>
              )}
              {pago && (
                <span className="text-[10px] font-semibold uppercase text-green-700 dark:text-green-400 shrink-0">pago</span>
              )}
            </span>
            <span className="block text-xs truncate">
              <span className={t1.velha ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}>
                {t1.texto}
              </span>
              {detalhe && <span className="text-muted-foreground"> · {detalhe}</span>}
            </span>
          </span>
          <span className="font-semibold text-sm tabular-nums shrink-0">
            {formatCurrency(parseFloat(t.total || "0"))}
          </span>
          {carregando === t.id
            ? <Loader2 className="w-4 h-4 animate-spin text-amber-700 dark:text-amber-400 shrink-0" />
            : editando
              ? <Check className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
              : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </button>
        {pago && (
          <div className={`flex justify-end ${recuo} pb-2.5`}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs bg-background"
              disabled={editando || entregando !== null || carregando !== null}
              title={editando ? "Salve ou cancele o pedido que está aberto no PDV" : undefined}
              onClick={() => { void entregar(t); }}
              data-testid={`pdv-open-tab-deliver-${t.id}`}
            >
              {entregando === t.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Marcar como entregue
            </Button>
          </div>
        )}
      </li>
    );
  };

  // «Imprimir conta» (Pedro, 29/10/2026): a conta completa da mesa, da comanda ou de um cliente da mesa compartilhada.
  // O servidor manda para a fila da impressora; sem impressora conectada, devolve o texto para a janela do navegador.
  const imprimirConta = async (chave: string, daConta: OpenTab[], escopo: "mesa" | "cliente") => {
    setImprimindo(chave);
    try {
      const r = await customFetch<{ via: "fila" } | { via: "janela"; texto: string }>("/api/orders/open-tabs/imprimir-conta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderIds: daConta.map((t) => t.id), escopo }),
      });
      if (r.via === "fila") toast({ title: "Conta enviada para a impressora." });
      else if (!imprimirNaJanela(r.texto)) {
        toast({ title: "Libere as janelas do navegador para imprimir a conta.", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível imprimir a conta."), variant: "destructive" });
    } finally {
      setImprimindo(null);
    }
  };

  const botaoImprimir = (chave: string, daConta: OpenTab[], escopo: "mesa" | "cliente", testId: string) => (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="bg-background"
      disabled={imprimindo !== null}
      onClick={() => { void imprimirConta(chave, daConta, escopo); }}
      data-testid={testId}
    >
      {imprimindo === chave ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
      Imprimir conta
    </Button>
  );

  // `complemento` (o nome ao lado do WhatsApp) é o que encolhe na tela estreita; o valor fica no detalhe, que é curto.
  // No celular os botões descem para a linha de baixo (a quebra é do flex-wrap).
  const cabecalho = (
    g: GrupoConta,
    principal: string,
    detalhe: string,
    botao: string | null,
    testId: string,
    complemento?: string | null,
    imprimir?: ReactNode,
  ) => {
    const editandoAqui = g.tabs.some((t) => t.id === editingId);
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 bg-amber-100/60 dark:bg-amber-900/20">
        <span className="min-w-0 flex-1 basis-40">
          <span className="block text-sm truncate">
            <span className="font-semibold">{principal}</span>
            {complemento && <span className="text-muted-foreground"> · {complemento}</span>}
          </span>
          <span className="block text-xs text-muted-foreground truncate">{detalhe}</span>
        </span>
        {(imprimir || botao) && (
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {imprimir}
            {botao && (
              <Button
                type="button"
                size="sm"
                disabled={editandoAqui || carregando !== null}
                title={editandoAqui ? "Salve ou cancele o pedido que está aberto no PDV" : undefined}
                onClick={() => abrirFechamento(g)}
                data-testid={testId}
              >
                {botao}
              </Button>
            )}
          </span>
        )}
      </div>
    );
  };

  const resumo = (tabs: OpenTab[]) =>
    `${tabs.length} ${tabs.length === 1 ? "pedido" : "pedidos"} · ${formatCurrency(tabs.reduce((s, t) => s + parseFloat(t.total || "0"), 0))}`;

  const conta = (g: GrupoConta) => {
    // A conta do cliente que segura a mesa (o «1 QR code para cada cliente»).
    if (g.cliente) {
      const c = g.cliente;
      const pendentes = g.tabs.some((t) => t.paymentMethod === "Pendente");
      const detalhe = [
        `${NomeDaMesa(porCliente)} ${c.mesa}`,
        // A mesa onde o cliente está (Pedro, 29/10/2026).
        c.local ? `Mesa ${c.local}` : null,
        g.tabs.length === 0 ? "sem pedido" : resumo(g.tabs),
        c.esperando ? "esperando o celular" : null,
      ]
        .filter(Boolean)
        .join(" · ");
      // O «+» de dentro da comanda (Pedro, 29/10/2026): pedido novo no PDV, na conta deste cliente.
      const mais = porCliente ? (
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8 bg-background"
          aria-label={`Pedido novo na comanda ${c.mesa}`}
          title="Pedido novo na comanda"
          onClick={() =>
            onPedidoNaComanda({
              mesa: c.mesa,
              cliente: { id: c.id, firstName: c.nome || "Cliente", whatsappNumber: (c.whatsapp ?? "").replace(/\D/g, ""), createdAt: "" },
            })
          }
          data-testid={`pdv-comanda-pedido-${c.mesa}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      ) : null;
      return (
        <li key={g.key} data-testid={`pdv-open-account-${c.mesa}`}>
          {cabecalho(
            g,
            rotuloDoGrupo(g, porCliente),
            detalhe,
            g.tabs.length === 0 || pendentes ? "Fechar conta" : null,
            `pdv-close-account-${c.mesa}`,
            c.whatsapp ? c.nome : null,
            <>
              {mais}
              {pendentes ? botaoImprimir(g.key, g.tabs, "mesa", `pdv-print-account-${c.mesa}`) : null}
            </>,
          )}
          {g.tabs.length > 0 && (
            <ul className="divide-y divide-amber-200/70 dark:divide-amber-800/70">
              {g.tabs.map((t) => linha(t, 1, "Pedido"))}
            </ul>
          )}
        </li>
      );
    }
    if (!g.key.startsWith("mesa:")) return linha(g.tabs[0], 0);
    const pendentes = g.tabs.some((t) => t.paymentMethod === "Pendente");
    const clientes = clientesDaMesa(g.tabs);
    const editandoAqui = g.tabs.some((t) => t.id === editingId);
    return (
      <li key={g.key} data-testid={`pdv-open-table-${g.key}`}>
        {cabecalho(
          g,
          rotuloConta(g.tabs[0].tableNumber, porCliente),
          resumo(g.tabs),
          pendentes ? `Fechar ${nomeDaMesa(porCliente)}` : null,
          `pdv-close-table-${g.key}`,
          null,
          pendentes ? botaoImprimir(g.key, g.tabs, "mesa", `pdv-print-table-${g.key}`) : null,
        )}
        {clientes.length > 1 ? (
          <ul className="divide-y divide-amber-200/70 dark:divide-amber-800/70">
            {clientes.map((c) => {
              const aPagar = c.tabs.some((t) => t.paymentMethod === "Pendente");
              return (
                <li key={c.key} data-testid={`pdv-open-table-cliente-${c.key}`}>
                  <div className="flex items-center gap-3 pl-5 pr-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold truncate">{c.nome}</span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {[c.whatsapp, `${c.tabs.length} ${c.tabs.length === 1 ? "pedido" : "pedidos"}`].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums">
                        {formatCurrency(c.tabs.reduce((s, t) => s + parseFloat(t.total || "0"), 0))}
                      </span>
                      {aPagar && (
                        <span className="flex justify-end gap-3">
                          <button
                            type="button"
                            disabled={imprimindo !== null}
                            onClick={() => { void imprimirConta(`${g.key}:${c.key}`, c.tabs, "cliente"); }}
                            className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                            data-testid={`pdv-print-cliente-${c.key}`}
                          >
                            {imprimindo === `${g.key}:${c.key}` ? "Imprimindo…" : "Imprimir conta"}
                          </button>
                          <button
                            type="button"
                            disabled={editandoAqui || carregando !== null}
                            onClick={() => abrirFechamento(g, c)}
                            className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                            data-testid={`pdv-close-cliente-${c.key}`}
                          >
                            Fechar conta
                          </button>
                        </span>
                      )}
                    </span>
                  </div>
                  <ul className="divide-y divide-amber-200/50 dark:divide-amber-800/50">
                    {c.tabs.map((t) => linha(t, 2, "Pedido"))}
                  </ul>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="divide-y divide-amber-200/70 dark:divide-amber-800/70">
            {g.tabs.map((t) => linha(t, 1))}
          </ul>
        )}
      </li>
    );
  };

  // Comanda individual (Pedro, 30/10/2026: «não estou gostando do visual de contas abertas»; das 3 opções, a grade): um
  // cartão por comanda, como a comanda de papel no balcão. Conta sem comanda (balcão, mesa digitada) fica na lista de
  // sempre, embaixo da grade.
  if (porCliente) {
    const totalDe = (ts: OpenTab[]) => ts.reduce((s, t) => s + parseFloat(t.total || "0"), 0);
    const alternar = (k: string) =>
      setExpandidas((atual) => {
        const n = new Set(atual);
        if (n.has(k)) n.delete(k);
        else n.add(k);
        return n;
      });
    // O «+ Pedido» fecha a grade: os produtos sobem e o Pedido mostra a comanda.
    const pedirNaComanda = (c: ClienteDaConta) => {
      setAberto(false);
      onPedidoNaComanda({ mesa: c.mesa, cliente: { id: c.id, firstName: c.nome || "Cliente", whatsappNumber: (c.whatsapp ?? "").replace(/\D/g, ""), createdAt: "" } });
    };
    const comandas = visiveis.filter((g) => g.cliente);
    const outras = visiveis.filter((g) => !g.cliente);
    const pedidoDaComanda = (t: OpenTab) => {
      const pago = t.paymentMethod !== "Pendente";
      const editando = t.id === editingId;
      return (
        <li key={t.id}>
          <button
            type="button"
            disabled={editando || carregando !== null}
            onClick={async () => {
              setCarregando(t.id);
              try {
                await onPick(t);
              } finally {
                setCarregando(null);
              }
            }}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted/60 ${editando ? "bg-muted" : ""}`}
            data-testid={`pdv-open-tab-${t.id}`}
          >
            <span className="font-mono text-muted-foreground">#{orderDisplayCode(t, codeStyle)}</span>
            {t.source === "link" && <span className="rounded bg-muted px-1 text-[10px] font-semibold">QR</span>}
            {pago && <span className="text-[10px] font-semibold uppercase text-green-700">pago</span>}
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{tempoAberto(t.createdAt).texto}</span>
            <span className="font-semibold tabular-nums">{formatCurrency(parseFloat(t.total || "0"))}</span>
            {carregando === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
          {pago && (
            <div className="flex justify-end px-3 pb-2">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={editando || entregando !== null || carregando !== null} onClick={() => { void entregar(t); }} data-testid={`pdv-open-tab-deliver-${t.id}`}>
                {entregando === t.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                Marcar como entregue
              </Button>
            </div>
          )}
        </li>
      );
    };
    const cartao = (g: GrupoConta) => {
      const c = g.cliente!;
      const n = g.tabs.length;
      const pendentes = g.tabs.some((t) => t.paymentMethod === "Pendente");
      const aberta = expandidas.has(g.key);
      const desde = g.tabs[0]?.createdAt;
      const velha = desde ? tempoAberto(desde).velha : false;
      return (
        <li key={g.key} className="relative flex flex-col overflow-hidden rounded-xl border bg-background" data-testid={`pdv-open-account-${c.mesa}`}>
          <span className={`h-1 ${c.esperando ? "bg-amber-400" : velha ? "bg-red-500" : "bg-primary"}`} />
          {pendentes && (
            <button type="button" title="Imprimir conta" aria-label="Imprimir conta" disabled={imprimindo !== null} onClick={() => { void imprimirConta(g.key, g.tabs, "mesa"); }} className="absolute right-2 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`pdv-print-account-${c.mesa}`}>
              {imprimindo === g.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            </button>
          )}
          <button type="button" onClick={() => n > 0 && alternar(g.key)} className="flex flex-1 flex-col p-3 text-left" aria-expanded={n > 0 ? aberta : undefined} data-testid={`pdv-comanda-cartao-${c.mesa}`}>
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Comanda</span>
            <span className="block text-2xl font-bold leading-none tabular-nums">{c.mesa}</span>
            <span className="mt-2 block w-full truncate text-sm font-semibold">{c.nome || c.whatsapp || "Cliente"}</span>
            <span className="block w-full truncate text-xs text-muted-foreground">
              {[c.local ? `Mesa ${c.local}` : null, desde ? tempoAberto(desde).texto : null].filter(Boolean).join(" · ") || c.whatsapp || "\u00a0"}
            </span>
            {c.esperando && <span className="mt-1.5 inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">aguardando o celular</span>}
            <span className="mt-auto flex w-full items-center gap-1 pt-2">
              <span className="font-semibold tabular-nums">{formatCurrency(totalDe(g.tabs))}</span>
              <span className="truncate text-[11px] text-muted-foreground">· {n === 0 ? "sem pedido" : `${n} ${n === 1 ? "pedido" : "pedidos"}`}</span>
              {n > 0 && <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${aberta ? "rotate-180" : ""}`} />}
            </span>
          </button>
          {aberta && n > 0 && <ul className="divide-y border-t">{g.tabs.map(pedidoDaComanda)}</ul>}
          <span className="grid grid-cols-2 border-t">
            <button type="button" onClick={() => pedirNaComanda(c)} className="flex items-center justify-center gap-1 py-2 text-xs font-semibold hover:bg-muted" data-testid={`pdv-comanda-pedido-${c.mesa}`}>
              <Plus className="h-3.5 w-3.5" />
              Pedido
            </button>
            <button
              type="button"
              disabled={(n > 0 && !pendentes) || g.tabs.some((t) => t.id === editingId) || carregando !== null}
              title={g.tabs.some((t) => t.id === editingId) ? "Salve ou cancele o pedido que está aberto no PDV" : undefined}
              onClick={() => abrirFechamento(g)}
              className="border-l py-2 text-xs font-semibold text-destructive hover:bg-destructive/5 disabled:opacity-40"
              data-testid={`pdv-close-account-${c.mesa}`}
            >
              <span className="sm:hidden">Fechar</span>
              <span className="hidden sm:inline">Fechar conta</span>
            </button>
          </span>
        </li>
      );
    };
    return (
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm" data-testid="pdv-comandas">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
          <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto} className="flex min-w-0 items-center gap-2" data-testid="pdv-open-tabs-toggle">
            <UtensilsCrossed className="h-4 w-4 shrink-0 text-primary" />
            <span className="whitespace-nowrap font-semibold">Comandas abertas</span>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold tabular-nums text-primary">{grupos.length}</span>
            <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">{formatCurrency(soma)}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${aberto ? "rotate-180" : ""}`} />
          </button>
          <span className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
            <span className="relative w-full sm:w-48">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome ou telefone" inputMode="search" className="h-8 bg-background pl-8 text-sm" data-testid="pdv-open-tabs-search" />
            </span>
            <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 px-2.5 text-xs" onClick={() => setCadastro({})} data-testid="pdv-open-tabs-cadastrar">
              <Plus className="mr-1 h-4 w-4" />
              Comanda
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 px-2.5 text-xs" onClick={() => setLendoQr(true)} data-testid="pdv-open-tabs-ler-qr">
              <ScanQrCode className="mr-1 h-4 w-4" />
              Ler QR code
            </Button>
          </span>
          {atrasadas > 0 && (
            <span className="flex basis-full items-center gap-1 text-[11px] font-semibold text-red-700">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {atrasadas === 1 ? "1 conta parada" : `${atrasadas} contas paradas`} há mais de 12h
            </span>
          )}
          {/* Com a grade fechada, as comandas aparecem como etiquetas (Pedro, 29/10/2026). */}
          {!mostrarLista && grupos.length > 0 && (
            <button type="button" onClick={() => setAberto(true)} className="flex basis-full flex-wrap gap-1.5 text-left" data-testid="pdv-open-tabs-rotulos">
              {grupos.slice(0, 12).map((g) => (
                <span key={g.key} className="max-w-full truncate rounded-full border bg-background px-2 py-0.5 text-[11px]">
                  {g.cliente ? (
                    <>
                      <span className="font-bold tabular-nums">{g.cliente.mesa}</span> · {g.cliente.nome || g.cliente.whatsapp}
                    </>
                  ) : (
                    rotuloDoGrupo(g, porCliente)
                  )}
                </span>
              ))}
              {grupos.length > 12 && <span className="px-1 py-0.5 text-[11px] font-medium">+{grupos.length - 12}</span>}
            </button>
          )}
        </div>
        {mostrarLista && (
          <div className="max-h-[32rem] overflow-y-auto border-t">
            {comandas.length > 0 && <ul className="grid grid-cols-2 gap-3 p-3 lg:grid-cols-3">{comandas.map(cartao)}</ul>}
            {outras.length > 0 && <ul className="divide-y divide-amber-200 border-t border-amber-200 bg-amber-50">{outras.map(conta)}</ul>}
            {visiveis.length === 0 && (
              <p className="px-4 py-3 text-sm text-muted-foreground" data-testid="pdv-open-tabs-nada">
                {busca.trim() ? `Nenhuma comanda com «${busca.trim()}».` : "Nenhuma comanda aberta."}
              </p>
            )}
          </div>
        )}
        {fechando && (
          <FecharMesaDialog fechamento={fechando} codeStyle={codeStyle} acceptedPayments={acceptedPayments} cardSurchargeConfig={cardSurchargeConfig} taxaServico={taxaServico} onClose={() => setFechando(null)} />
        )}
        {soltando?.cliente && <SoltarMesaDialog cliente={soltando.cliente} onClose={() => setSoltando(null)} />}
        {lendoQr && <LerQrDaMesa onLido={lerQr} onClose={() => setLendoQr(false)} porCliente={porCliente} />}
        {cadastro && <CadastroNaComanda mesaLida={cadastro.mesa} onClose={() => setCadastro(null)} />}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full px-3 pt-2.5 pb-1.5 text-sm hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
        aria-expanded={aberto}
        data-testid="pdv-open-tabs-toggle"
      >
        <span className="flex items-center gap-2">
          <UtensilsCrossed className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
          <span className="font-semibold text-amber-900 dark:text-amber-200 whitespace-nowrap">Comandas abertas</span>
          <span className="text-[11px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 px-1.5 py-0.5 rounded-full tabular-nums">
            {lista.length + semPedido}
          </span>
          <span className="ml-auto font-semibold text-amber-900 dark:text-amber-200 tabular-nums">
            {formatCurrency(soma)}
          </span>
          <ChevronDown className={`w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0 transition-transform ${aberto ? "rotate-180" : ""}`} />
        </span>
        {atrasadas > 0 && (
          // Linha própria: espremer isso no cabeçalho quebrava "Contas
          // abertas" em duas linhas no celular.
          <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-700 dark:text-red-400">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {atrasadas === 1 ? "1 conta parada" : `${atrasadas} contas paradas`} há mais de 12h
          </span>
        )}
      </button>
      {/* Com a comanda individual são três botões: no celular a busca (ou as etiquetas) fica numa linha e os botões na
          de baixo. */}
      <div className="flex flex-wrap items-start gap-2 px-3 pb-2.5">
        {buscando ? (
          <div className={`relative min-w-0 flex-1 ${porCliente ? "basis-full sm:basis-0" : ""}`}>
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Telefone ou nome"
              inputMode="search"
              className="h-8 pl-8 text-sm bg-background"
              data-testid="pdv-open-tabs-search"
            />
          </div>
        ) : !aberto && rotulos.length > 0 ? (
          <button
            type="button"
            onClick={() => setAberto(true)}
            className={`min-w-0 flex-1 flex flex-wrap gap-1 pt-1 text-left ${porCliente ? "basis-full sm:basis-0" : ""}`}
            data-testid="pdv-open-tabs-rotulos"
          >
            {rotulos.slice(0, 12).map((r, i) => (
              <span key={i} className="max-w-full truncate rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
                {r}
              </span>
            ))}
            {rotulos.length > 12 && (
              <span className="px-1 py-0.5 text-[11px] font-medium text-amber-900 dark:text-amber-100">+{rotulos.length - 12}</span>
            )}
          </button>
        ) : (
          <span className="flex-1" />
        )}
        <div className="ml-auto flex shrink-0 items-start gap-2">
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8 shrink-0 bg-background"
          aria-label={buscando ? "Fechar a busca" : "Buscar conta por telefone ou nome"}
          onClick={() => {
            if (buscando) setBusca("");
            setBuscando((v) => !v);
          }}
          data-testid="pdv-open-tabs-lupa"
        >
          {buscando ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
        </Button>
        {porCliente && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 shrink-0 bg-background px-2.5 text-xs"
            onClick={() => setCadastro({})}
            data-testid="pdv-open-tabs-cadastrar"
          >
            <Plus className="w-4 h-4 mr-1" />
            Comanda
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 shrink-0 bg-background px-2.5 text-xs"
          onClick={() => setLendoQr(true)}
          data-testid="pdv-open-tabs-ler-qr"
        >
          <ScanQrCode className="w-4 h-4 mr-1" />
          Ler QR code
        </Button>
        </div>
      </div>
      {mostrarLista && (
        <ul className="border-t border-amber-200 dark:border-amber-800 divide-y divide-amber-200 dark:divide-amber-800 max-h-80 overflow-y-auto">
          {visiveis.map(conta)}
          {visiveis.length === 0 && (
            <li className="px-3 py-3 text-sm text-muted-foreground" data-testid="pdv-open-tabs-nada">
              Nenhuma conta com «{busca.trim()}».
            </li>
          )}
        </ul>
      )}
      {fechando && (
        <FecharMesaDialog
          taxaServico={taxaServico}
          fechamento={fechando}
          codeStyle={codeStyle}
          acceptedPayments={acceptedPayments}
          cardSurchargeConfig={cardSurchargeConfig}
          onClose={() => setFechando(null)}
        />
      )}
      {soltando?.cliente && <SoltarMesaDialog cliente={soltando.cliente} onClose={() => setSoltando(null)} />}
      {lendoQr && <LerQrDaMesa onLido={lerQr} onClose={() => setLendoQr(false)} porCliente={porCliente} />}
      {cadastro && <CadastroNaComanda mesaLida={cadastro.mesa} onClose={() => setCadastro(null)} />}
    </div>
  );
}

// «+ Comanda» (Pedro, 29/10/2026): o PDV cadastra o cliente na comanda do cartão: o número, depois o primeiro
// nome e o WhatsApp. A comanda fica esperando o celular: o primeiro que ler o QR entra já logado como o cliente, e
// nenhum outro entra. Comanda que já está com o cliente: os dados dele vêm preenchidos, e salvar de novo faz ela esperar
// outro celular.
export function CadastroNaComanda({ mesaLida, onClose }: { mesaLida?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [mesa, setMesa] = useState(mesaLida ?? "");
  const [etapa, setEtapa] = useState<"numero" | "cliente">(mesaLida ? "cliente" : "numero");
  const [nome, setNome] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [ddi, setDdi] = useState("55");
  const [dono, setDono] = useState<MesaPresa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // A comanda já está com alguém? Os dados dele vêm preenchidos (salvar de novo = trocar de celular).
  const conferirDono = async (n: string) => {
    const presas = await qc.fetchQuery({ queryKey: ["pdv-open-tabs-contas"], queryFn: buscarMesasPresas, staleTime: 0 }).catch(() => null);
    const p = presas?.contas.find((c) => mesaKey(c.mesa) === n) ?? null;
    setDono(p);
    if (p) {
      setNome(p.nome ?? "");
      const digitos = (p.whatsappNumber ?? "").replace(/\D/g, "");
      setWhatsapp(digitos.startsWith("55") && digitos.length > 11 ? digitos.slice(2) : digitos);
      setDdi("55");
    }
  };
  useEffect(() => {
    if (mesaLida) void conferirDono(mesaKey(mesaLida));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continuar = async () => {
    const n = mesa.replace(/\D/g, "");
    if (!n) return;
    setMesa(n);
    setErro(null);
    await conferirDono(n);
    setEtapa("cliente");
  };
  const salvar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const r = await customFetch<{ mesa: string; cliente: { nome: string } }>("/api/orders/open-tabs/comanda", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mesa: mesaKey(mesa), nome, whatsapp, ddi }),
      });
      toast({
        title: `Comanda ${r.mesa} cadastrada para ${r.cliente.nome}`,
        description: `O primeiro celular que ler o QR da comanda entra como ${r.cliente.nome}.`,
      });
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs-contas"] });
      onClose();
    } catch (err) {
      setErro(extractApiErrorMessage(err, "Não foi possível cadastrar."));
    } finally {
      setEnviando(false);
    }
  };

  const numero = mesaKey(mesa);
  return (
    <ModalShell
      open
      onOpenChange={(o) => { if (!o && !enviando) onClose(); }}
      title={etapa === "numero" ? "Adicionar comanda" : `Comanda ${numero}`}
      subtitle={
        etapa === "numero"
          ? "O número que está no cartão da comanda."
          : dono
            ? `Está com ${dono.nome || "um cliente"}. Salvar de novo faz a comanda esperar o próximo celular que ler o QR.`
            : "O primeiro celular que ler o QR desta comanda entra já logado como este cliente."
      }
      size="sm"
      footer={
        etapa === "numero" ? (
          <Button type="button" className="w-full" onClick={() => { void continuar(); }} disabled={!/^\d{1,3}$/.test(mesa.trim())} data-testid="pdv-cadastro-continuar">
            Continuar
          </Button>
        ) : (
          <Button type="button" className="w-full" onClick={() => { void salvar(); }} disabled={enviando || !nome.trim() || whatsapp.replace(/\D/g, "").length < 8} data-testid="pdv-cadastro-salvar">
            {enviando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Salvar
          </Button>
        )
      }
    >
      {etapa === "numero" ? (
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); void continuar(); }}>
          <Label htmlFor="pdv-cadastro-numero">Número da comanda</Label>
          <Input
            id="pdv-cadastro-numero"
            autoFocus
            inputMode="numeric"
            maxLength={3}
            value={mesa}
            onChange={(e) => setMesa(e.target.value.replace(/\D/g, "").slice(0, 3))}
            className="h-12 text-center text-2xl font-bold tracking-widest"
            data-testid="pdv-cadastro-numero"
          />
        </form>
      ) : (
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void salvar(); }}>
          <div className="space-y-1.5">
            <Label htmlFor="pdv-cadastro-nome">Primeiro nome</Label>
            <Input
              id="pdv-cadastro-nome"
              autoFocus
              value={nome}
              // A inicial de cada palavra em maiúscula enquanto digita (Pedro, 29/10/2026).
              onChange={(e) => setNome(e.target.value.replace(/(^|\s)(\S)/g, (_, espaco: string, letra: string) => espaco + letra.toUpperCase()))}
              autoCapitalize="words"
              autoComplete="off"
              data-testid="pdv-cadastro-nome"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pdv-cadastro-whatsapp">WhatsApp</Label>
            <WhatsAppInput id="pdv-cadastro-whatsapp" value={whatsapp} onChange={setWhatsapp} ddi={ddi} onDdiChange={setDdi} />
          </div>
          {erro && (
            <p className="text-sm text-destructive" data-testid="pdv-cadastro-erro">
              {erro}
            </p>
          )}
        </form>
      )}
    </ModalShell>
  );
}

// Conta sem pedido do «1 QR code para cada cliente»: fechar solta o QR da mesa para o próximo cliente.
function SoltarMesaDialog({ cliente, onClose }: { cliente: ClienteDaConta; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [enviando, setEnviando] = useState(false);
  const soltar = async () => {
    setEnviando(true);
    try {
      await customFetch("/api/orders/open-tabs/liberar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mesa: cliente.mesa, customerId: cliente.id }),
      });
      toast({ title: "Conta fechada", description: `A comanda ${cliente.mesa} está livre para outro cliente.` });
      onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível fechar a conta."), variant: "destructive" });
    } finally {
      setEnviando(false);
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs-contas"] });
    }
  };
  return (
    <ModalShell
      open
      onOpenChange={(o) => { if (!o && !enviando) onClose(); }}
      title="Fechar conta"
      subtitle={[cliente.whatsapp, cliente.nome, `Comanda ${cliente.mesa}`].filter(Boolean).join(" · ")}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button type="button" onClick={() => { void soltar(); }} disabled={enviando} data-testid="pdv-soltar-mesa-confirm">
            {enviando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Fechar conta
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
        </div>
      }
    >
      <p className="text-sm text-muted-foreground">
        Esta conta não tem pedido. Fechar libera a comanda {cliente.mesa} para outro cliente.
      </p>
    </ModalShell>
  );
}

function FecharMesaDialog({
  fechamento,
  codeStyle,
  acceptedPayments,
  cardSurchargeConfig,
  taxaServico,
  onClose,
}: {
  fechamento: Fechamento;
  codeStyle?: string | null;
  acceptedPayments: string[];
  cardSurchargeConfig?: unknown;
  taxaServico?: { ativo?: boolean; percentual?: number | string } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [forma, setForma] = useState(acceptedPayments[0] ?? "");
  const [enviando, setEnviando] = useState<"entregar" | "pagar" | null>(null);
  const { grupo, feito } = fechamento;
  // Taxa de serviço (Pedro, 30/10/2026): ligada aqui quando a loja cobra; desligar vale só para esta conta.
  const pctServico = taxaServico?.ativo ? Number(String(taxaServico.percentual ?? 10).replace(",", ".")) || 0 : 0;
  const temTaxaNaConta = pctServico > 0 && grupo.tabs.some((t) => t.deliveryMode !== "entrega");
  const [cobrarTaxa, setCobrarTaxa] = useState(true);

  // Mesma conta do servidor: acréscimo de cartão por pedido, sobre os produtos; a taxa de serviço também sobre os
  // produtos, e não na entrega.
  const centavos = (v: number) => Math.round(v * 100) / 100;
  const porPedido = grupo.tabs.map((t) => {
    const fee = parseFloat(t.deliveryFee || "0") || 0;
    const base = Math.max(0, (parseFloat(t.total || "0") || 0) - fee);
    const acrescimo = forma ? computeCardSurcharge({ cardSurchargeConfig }, forma, base, fee) : 0;
    const taxa = temTaxaNaConta && cobrarTaxa && t.deliveryMode !== "entrega" ? centavos((base * pctServico) / 100) : 0;
    return { t, total: base + fee + acrescimo, acrescimo, taxa };
  });
  const acrescimo = porPedido.reduce((s, p) => s + p.acrescimo, 0);
  const taxa = porPedido.reduce((s, p) => s + p.taxa, 0);
  const total = porPedido.reduce((s, p) => s + p.total + p.taxa, 0);

  const fechar = async (entregar: boolean) => {
    setEnviando(entregar ? "entregar" : "pagar");
    try {
      const r = await customFetch<{ fechados: number; total: number }>("/api/orders/open-tabs/close", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderIds: grupo.tabs.map((t) => t.id), paymentMethod: forma, cobrarTaxaServico: temTaxaNaConta && cobrarTaxa }),
      });
      // Pagamento registrado; o Entregue vai pedido a pedido. O que falhar fica na lista com «pago».
      let falhou = 0;
      let erro: unknown = null;
      if (entregar) {
        for (const t of grupo.tabs) {
          try {
            await marcarComoEntregue(t.id);
          } catch (e) {
            falhou++;
            erro = e;
          }
        }
      }
      if (falhou > 0) {
        toast({
          title: `${feito}, mas ${falhou === 1 ? "1 pedido não foi marcado" : `${falhou} pedidos não foram marcados`} como entregue`,
          description: `${extractApiErrorMessage(erro, "Tente de novo.")} Use «Marcar como entregue» na lista.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: feito,
          description: `${r.fechados} ${r.fechados === 1 ? "pedido" : "pedidos"} · ${formatCurrency(r.total)} em ${forma}${entregar ? " · entregues" : ""}.`,
        });
      }
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs-contas"] });
      qc.invalidateQueries({ queryKey: getListOrdersQueryKey() });
      onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível fechar a conta."), variant: "destructive" });
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
    } finally {
      setEnviando(null);
    }
  };

  return (
    <ModalShell
      open
      onOpenChange={(o) => { if (!o && !enviando) onClose(); }}
      title={fechamento.titulo}
      subtitle={fechamento.subtitulo}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button type="button" onClick={() => { void fechar(true); }} disabled={!forma || enviando !== null} data-testid="pdv-close-table-deliver">
            {enviando === "entregar" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Pago e marcar como entregue
          </Button>
          <Button type="button" variant="outline" onClick={() => { void fechar(false); }} disabled={!forma || enviando !== null} data-testid="pdv-close-table-confirm">
            {enviando === "pagar" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Só registrar pagamento
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} disabled={enviando !== null}>Cancelar</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <ul className="divide-y rounded-md border text-sm">
          {porPedido.map(({ t, total: tt }) => (
            <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate">
                <span className="font-mono text-xs text-muted-foreground mr-1.5">#{orderDisplayCode(t, codeStyle)}</span>
                {t.customerName || "Pedido"}
              </span>
              <span className="font-semibold tabular-nums shrink-0">{formatCurrency(tt)}</span>
            </li>
          ))}
          {taxa > 0 && (
            <li className="flex items-center justify-between gap-3 px-3 py-2" data-testid="pdv-close-table-taxa">
              <span>Taxa de serviço ({String(pctServico).replace(".", ",")}%)</span>
              <span className="tabular-nums shrink-0">{formatCurrency(taxa)}</span>
            </li>
          )}
          {/* O total ficava no botão «Fechar R$ X». */}
          <li className="flex items-center justify-between gap-3 px-3 py-2 font-semibold bg-muted/40">
            <span>Total</span>
            <span className="tabular-nums" data-testid="pdv-close-table-total">{formatCurrency(total)}</span>
          </li>
        </ul>
        {temTaxaNaConta && (
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label htmlFor="pdv-close-table-cobrar-taxa" className="text-sm font-medium">Cobrar taxa de serviço</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">{String(pctServico).replace(".", ",")}% sobre os produtos. Desligue se o cliente não quiser pagar.</p>
            </div>
            <Switch id="pdv-close-table-cobrar-taxa" checked={cobrarTaxa} onCheckedChange={setCobrarTaxa} className="mt-0.5 shrink-0" data-testid="pdv-close-table-cobrar-taxa" />
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-sm">Forma de pagamento</Label>
          <div className="grid grid-cols-2 gap-2">
            {acceptedPayments.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setForma(m)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${forma === m ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}
                data-testid={`pdv-close-table-pay-${m}`}
              >
                {getPaymentMethodLabel(m)}
              </button>
            ))}
          </div>
          {acrescimo > 0 && (
            <p className="text-xs text-muted-foreground">Inclui acréscimo de {formatCurrency(acrescimo)} ({forma}).</p>
          )}
          <p className="text-xs text-muted-foreground">Pagamento dividido, desconto ou troco: feche cada pedido na lista.</p>
        </div>
      </div>
    </ModalShell>
  );
}

// Endereços salvos do cliente identificado — reuso na entrega, como no link.
function SavedAddressesPicker({ customerId, savedAddressId, onPick }: { customerId: string; savedAddressId: string | null; onPick: (addr: any) => void }) {
  const { data: addresses = [] } = useQuery<any[]>({
    queryKey: ["customer-addresses", customerId],
    queryFn: () => customFetch(`/api/customers/${customerId}/addresses`),
    enabled: !!customerId,
    staleTime: 60_000,
  });
  if (!addresses.length) return null;
  return (
    <div className="space-y-1.5 pt-2">
      <Label className="text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Endereços salvos</Label>
      <div className="space-y-1">
        {addresses.map((a: any) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onPick(a)}
            className={`w-full text-left text-xs px-3 py-2 rounded border transition-colors ${savedAddressId === a.id ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}
            data-testid={`pdv-saved-address-${a.id}`}
          >
            <span className="font-medium">{a.street}{a.number ? `, ${a.number}` : ""}</span>
            {a.neighborhood ? ` — ${a.neighborhood}` : ""}
            {a.isDefault && <span className="ml-1 text-[10px] uppercase font-bold text-primary">padrão</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PDV() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: store } = useGetStore();
  const { data: produtosDaLoja = [], refetch: refetchProducts } = useListProducts();
  // Pacote de escolhas e Adicionais fracionados só com o recurso (plano ou liberação válida), como na vitrine: sem ele, o
  // produto entra no PDV como sempre — sem pacote e toda opção contando 1. A lista do painel traz a configuração de
  // qualquer jeito (o editor precisa dela).
  const podePreparos = useFeatureAllowed("prep_count");
  const podePesos = useFeatureAllowed("option_weight");
  const semPreparos = (p: any) => {
    if (!p) return p;
    let q = p;
    if (q.prepCount && !podePreparos) q = { ...q, prepCount: null };
    if (!podePesos && Array.isArray(q.optionGroups) && q.optionGroups.some(groupHasWeights)) q = { ...q, optionGroups: semPesos(q.optionGroups) };
    return q;
  };
  const products = useMemo(() => (produtosDaLoja as any[]).map(semPreparos), [produtosDaLoja, podePreparos, podePesos]);
  const { data: categories = [] } = useListCategories();

  // Leitor de código no PDV (Ultra, 10/09/2026): o leitor digita e manda
  // Enter; casa com products.barcode na lista que o PDV já tem — sem chamar a
  // API, sem gastar o limite do lookup. Desde 22/09/2026 o campo também aceita
  // o código próprio da loja ("CESTA-01"), digitado; a normalização é a mesma
  // do cadastro (@workspace/text, productCode). Foco automático ao abrir só onde há mouse (desktop), como no
  // "+ Produto"; depois de cada bipe o foco fica no campo.
  const ultra = useIsUltra();
  // Ultra com o pedido na mesa: as comandas e mesas ficam na página delas (Pedro, 30/10/2026); no PDV, só o balcão.
  const paginaDeComandas = useFeatureAllowed("table_orders") && (store as any)?.tableOrdersEnabled === true;
  const podeBarcode = useFeatureAllowed("barcode");
  const desktopPointer = useMemo(() => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches, []);
  const scanRef = useRef<HTMLInputElement>(null);
  const [scanCode, setScanCode] = useState("");
  const [scanStatus, setScanStatus] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (podeBarcode && desktopPointer) scanRef.current?.focus();
    // Saiu do Ultra com a aba aberta: o «adicionado» da última leitura não fica embaixo do campo desligado.
    if (!podeBarcode) { setScanCode(""); setScanStatus(null); }
  }, [podeBarcode, desktopPointer]);
  const handleScan = async () => {
    // Leitura por código é do Ultra (ou direito da loja): sem a feature não casa, nem com a lista ainda guardada.
    if (!podeBarcode) { setScanCode(""); return; }
    const codigo = normalizarCodigoProduto(scanCode);
    // Limpa SEMPRE (leitura curta inclusive): sobra de uma leitura falha
    // grudava na próxima e virava um código que não existe (medido na sonda).
    setScanCode("");
    scanRef.current?.focus();
    if (!codigo) { setScanStatus({ ok: false, text: "Código inválido. Leia ou digite de novo." }); return; }
    let found = (products as any[]).find((p) => p.barcode === codigo) as Product | undefined;
    if (!found) {
      // A lista pode estar velha (código cadastrado em outra aba, PDV aberto
      // há horas): busca de novo UMA vez antes de dizer que não existe.
      setScanStatus({ ok: true, text: "Procurando…" });
      const fresh = await refetchProducts().catch(() => null);
      found = semPreparos(((fresh?.data as any[] | undefined) ?? []).find((p) => p.barcode === codigo)) as Product | undefined;
    }
    if (!found) { setScanStatus({ ok: false, text: `Nenhum produto com o código ${formatarCodigoProduto(codigo)}.` }); return; }
    if (found.status !== "ativo") { setScanStatus({ ok: false, text: `${found.name} está indisponível.` }); return; }
    if (cart.stockReached(found)) { setScanStatus({ ok: false, text: `${found.name}: sem estoque.` }); return; }
    const abreOpcoes = productHasOptions(found) || ((found as any).isOrderBased && !cart.dataUnica) || (found as any).soldByWeight;
    cart.handleQuickAdd(found);
    setScanStatus({ ok: true, text: abreOpcoes ? `${found.name}: escolha as opções.` : `${found.name} adicionado.` });
  };

  const slug = (user as any)?.store?.slug || (store as any)?.slug || "";

  const cart = usePdvCart({
    store,
    products: products as Product[],
    slug,
    ordersEndpoint: "/api/orders",
    source: "pdv",
    matchBarcode: podeBarcode,
  });

  // Toggle "Imprimir pedidos criados no PDV". Persistente em store.pdvSettings.autoPrint.
  // Default false. Quando ON, o backend (orders.ts) enfileira o pedido em print_queue
  // pro Print Agent imprimir + browser thermal (se conectado) também imprime —
  // aqui no painel os dois saem (no Link do PDV, quem imprime no navegador não vai pro app).
  const serverAutoPrint = !!((store as any)?.pdvSettings?.autoPrint);
  const [autoPrint, setAutoPrint] = useState(serverAutoPrint);
  // «Informar WhatsApp» do aviso de pontos: a edição do cliente abre por cima da venda (sair da página perde o carrinho).
  const [informandoWhatsApp, setInformandoWhatsApp] = useState(false);
  const [autoPrintSaving, setAutoPrintSaving] = useState(false);
  useEffect(() => {
    setAutoPrint(serverAutoPrint);
  }, [serverAutoPrint]);

  async function toggleAutoPrint(next: boolean) {
    const prev = autoPrint;
    setAutoPrint(next); // Optimistic
    setAutoPrintSaving(true);
    try {
      await customFetch("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pdvSettings: {
            ...((store as any)?.pdvSettings || {}),
            autoPrint: next,
          },
        }),
      });
      // Re-fetch store pra todas as telas pegarem o novo valor.
      qc.invalidateQueries({ queryKey: ["store"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err: any) {
      setAutoPrint(prev); // Revert on error
      toast({
        title: errorTitle("salvar a preferência"),
        description: err?.message || TRY_AGAIN_DESCRIPTION,
        variant: "destructive",
      });
    } finally {
      setAutoPrintSaving(false);
    }
  }

  const useCustomerSearch = (term: string, enabled: boolean) => {
    const { data } = useListCustomers({ search: term }, { query: { enabled } as any });
    return { customers: (data as any) || [] };
  };

  // /pdv?edit=<orderId> (vindo da página Pedidos): carrega o pedido pra edição
  // assim que o catálogo estiver disponível pra casar os produtos.
  const loadedEditRef = useRef<string | null>(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const editId = sp.get("edit");
    if (!editId || (products as Product[]).length === 0) return;
    if (loadedEditRef.current === editId) return;
    loadedEditRef.current = editId;
    customFetch<any>(`/api/orders/${editId}`)
      .then((o) => {
        // Pedido do link também edita (07/09); só cupom fica de fora — a
        // rota de edição não recalcula desconto de cupom.
        const editavel = (o?.source === "pdv" || o?.source === "link") && o.status !== "cancelado";
        if (editavel && o.source === "link" && o.couponCode) {
          toast({ title: "Pedido com cupom não pode ser editado", description: "Cancele e registre de novo.", variant: "destructive" });
        } else if (editavel && o.source === "link" && Number(o.loyaltyPointsUsed) > 0) {
          toast({ title: "Pedido com prêmio de pontos não pode ser editado", description: "Cancele e registre de novo.", variant: "destructive" });
        } else if (editavel) {
          cart.loadOrderForEdit(o);
        } else {
          toast({ title: "Este pedido não pode ser editado no PDV", variant: "destructive" });
        }
      })
      .catch(() => {
        toast({ title: errorTitle("carregar o pedido"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // ⚠️ /api/orders/open-tabs devolve uma lista ENXUTA (sem itens). Jogar essa
  // linha direto no carrinho carregava um pedido VAZIO: a conta "não abria"
  // (relato da SG burguer em 12/09, conta de 59 dias). Busca o pedido inteiro
  // antes de editar, igual ao fluxo /pdv?edit=.
  // O «+» de dentro da comanda (Pedro, 29/10/2026): o pedido do PDV já com o cliente, a comanda e o «pagar depois».
  // Pedido aberto para editar sai do carrinho (o pedido salvo não muda); itens de uma venda nova ficam.
  const novoPedidoNaComanda = ({ mesa, cliente }: { mesa: string; cliente: Customer }) => {
    if (cart.editingOrder) cart.handleClearCart();
    cart.setTableNumber(mesa);
    cart.setPayLater(true);
    cart.setCustomer({ kind: "existing", customer: cliente });
    // No computador o aviso fica no topo do pedido (Pedro, 30/10/2026: o toast no canto passava despercebido). No
    // celular o pedido fica fechado: o toast diz o que aconteceu.
    if (window.matchMedia("(max-width: 767px)").matches) {
      toast({ title: `Pedido novo na comanda ${mesa}`, description: `Na conta de ${cliente.firstName}. Escolha os produtos e finalize.` });
    }
  };
  const comandaDoPedido =
    !cart.editingOrder && (store as any)?.tableQrPerClient === true && cart.payLater && /^\d+$/.test(cart.tableNumber.trim()) && cart.customer.kind === "existing"
      ? { mesa: cart.tableNumber.trim(), nome: cart.customer.customer.firstName }
      : null;

  const abrirContaAberta = async (tab: any) => {
    try {
      const completo = await customFetch<any>(`/api/orders/${tab.id}`);
      cart.loadOrderForEdit(completo);
    } catch {
      toast({ title: errorTitle("abrir a conta"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin.replace("://www.", "://") : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const printToken = cart.pdvSettings.printToken || "";
  const pdvUrl = slug
    ? `${origin}${basePath}/pdv/${slug}${printToken ? `?print=${encodeURIComponent(printToken)}` : ""}`
    : "";

  // 2 tabs em mobile (Produtos / Pedido). Em desktop (md:+) os 2 painéis
  // ficam lado a lado, tabs ficam invisíveis. O state `showCartMobile`
  // controla a tab ativa em mobile — true = Pedido, false = Produtos.
  // Em desktop é ignorado (ambos painéis renderizam).
  const tabPedidoAtiva = cart.showCartMobile;
  const finalizeDisabled = cart.submitting
    || (cart.minOrderValue > 0 && cart.subtotal < cart.minOrderValue)
    || (cart.deliveryMode === "entrega" && (cart.feeInfo.noDeliveryBlocked || cart.feeInfo.feeNotResolved));
  // Código do pedido em edição (faixa do carrinho, faixa da grade e barra de baixo).
  const editingCode = cart.editingOrder ? orderDisplayCode(cart.editingOrder, (store as any)?.orderCodeStyle) : null;
  // Fechando conta Pendente (com a forma escolhida): dois botões no lugar do «Fechar conta» (19/10/2026).
  const fechandoConta = !!cart.editingOrder?.wasPending && !cart.payLater;
  const finalizeLabel = cart.editingOrder ? "Salvar alterações" : "Finalizar pedido";
  const [acaoFechar, setAcaoFechar] = useState<"entregar" | "pagar" | null>(null);
  const fecharConta = async (entregar: boolean) => {
    setAcaoFechar(entregar ? "entregar" : "pagar");
    try {
      await (entregar ? cart.handleCloseAndDeliver() : cart.handleFinalize());
    } finally {
      setAcaoFechar(null);
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
    }
  };
  // «Finalizar pedido» e «Salvar alterações»: Comandas abertas mostra a conta nova (ou o total novo) na hora, sem
  // esperar a busca de 30 s (Pedro, 19/10).
  const finalizar = async () => {
    try {
      await cart.handleFinalize();
    } finally {
      qc.invalidateQueries({ queryKey: ["pdv-open-tabs"] });
    }
  };
  const barraDupla = fechandoConta && tabPedidoAtiva && cart.cart.length > 0;

  // <md: o AdminLayout põe pt-14 + p-4 + pb-20 (168px) em volta deste bloco;
  // com 100svh-4rem o documento ficava 104px mais alto que a tela e, ao
  // arrastar, o painel subia e sobrava um vão em branco (medido 10/09).
  // Altura = tela − (header + padding de cima); a margem negativa anula o
  // padding de baixo + o espaço da nav, e o documento fecha em 100svh exato.
  // NÃO usar `fixed` aqui: vira stacking context e a nav do layout (z-30)
  // passa por cima da barra "Ver pedido" (z-30) — medido, regressão de 10 min.
  return (
    <div className="flex flex-col h-[calc(100svh-4.5rem)] -mb-24 sm:h-[calc(100svh-5rem)] sm:-mb-[6.5rem] md:h-[calc(100svh-4rem)] md:mb-0 bg-background overflow-hidden">
      {/* Tabs mobile morreram (02/09, pedido do Pedro): a barra "Ver pedido"
          entra no Pedido e o X do painel volta — a aba era redundante. */}
      {/* <md sempre tem algo fixo embaixo (nav do layout ou a barra do
          carrinho): padding-bottom pra nada ficar escondido atrás. */}
      <div className={`flex flex-1 overflow-hidden ${barraDupla ? "pb-28" : "pb-16"} md:pb-0`}>
        {/* <md: o painel inteiro rola, com o topo junto. Rolando só a lista, um topo mais alto que a tela (conta aberta
            com a faixa de «parada há mais de 12h», o link, o selo) deixava a lista com altura zero e os produtos
            sumiam — caso Oficina burguer pitdog, 17/10/2026. md+: só a lista rola, como antes. */}
        <div className={`flex-1 flex flex-col overflow-y-auto md:overflow-hidden ${cart.showCartMobile ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-border space-y-3">
            {/* <md: ao editar, a grade só aparece depois do X do carrinho — e a
                faixa amarela ficava lá dentro, então parecia que a edição tinha
                sido abandonada (medido 10/09). Repete a faixa aqui. */}
            {cart.editingOrder && (
              <div className="md:hidden flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded px-2.5 py-1.5" data-testid="pdv-edit-banner-grid">
                <span className="flex items-center gap-1.5 min-w-0 truncate">
                  <Pencil className="w-3.5 h-3.5 shrink-0" />
                  Editando pedido
                  <span className="font-mono font-semibold">#{editingCode}</span>
                </span>
                <button onClick={cart.cancelEdit} className="underline shrink-0" data-testid="pdv-cancel-edit-grid">
                  cancelar
                </button>
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <Calculator className="w-6 h-6 text-primary" />
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold">PDV — Ponto de Venda</h1>
                <p className="text-xs text-muted-foreground">Venda presencial no balcão</p>
              </div>
              {/* <md: o toggle numa linha própria — ao lado do título ele
                  esmagava "PDV — Ponto de Venda" numa palavra por linha. */}
              <div className="flex items-center gap-2 text-xs basis-full md:basis-auto">
                <Printer className="w-4 h-4 text-muted-foreground shrink-0" />
                <Label htmlFor="pdv-autoprint" className="cursor-pointer select-none">
                  Imprimir pedidos criados no PDV
                </Label>
                <Switch
                  id="pdv-autoprint"
                  checked={autoPrint}
                  disabled={autoPrintSaving}
                  onCheckedChange={toggleAutoPrint}
                  data-testid="pdv-autoprint-toggle"
                />
              </div>
            </div>
            {pdvUrl && (
              <PublicLinkCard
                url={pdvUrl}
                label="Link do PDV:"
                icon={<Calculator className="w-4 h-4 text-secondary" />}
                shareLabel="o PDV da nossa loja"
              />
            )}
            <OpenTabsPicker
              onPick={abrirContaAberta}
              editingId={cart.editingOrder?.id ?? null}
              codeStyle={(store as any)?.orderCodeStyle}
              acceptedPayments={((store as any)?.acceptedPayments?.length ? (store as any).acceptedPayments : ["Dinheiro", "PIX"]) as string[]}
              cardSurchargeConfig={(store as any)?.cardSurchargeConfig}
              porCliente={!paginaDeComandas && (store as any)?.tableQrPerClient === true}
              soBalcao={paginaDeComandas}
              onPedidoNaComanda={novoPedidoNaComanda}
              taxaServico={(store as any)?.pdvSettings?.taxaServico ?? null}
            />
            {!ultra && (
              <UltraBadge />
            )}
            {/* <form>: no celular o Enter do teclado nem sempre chega como
                keydown "Enter" (teclados com IME mandam 229) — o submit
                implícito do form cobre esse caso; o keydown cobre o leitor. */}
            <form className="relative" onSubmit={(e) => { e.preventDefault(); void handleScan(); }} data-testid="pdv-barcode-form">
              <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={scanRef}
                value={scanCode}
                autoComplete="off"
                autoCapitalize="characters"
                enterKeyHint="go"
                placeholder="Ler ou digitar código"
                className="pl-9 pointer-coarse:pr-12 font-mono"
                disabled={!podeBarcode}
                onChange={(e) => { setScanCode(limparCodigoProduto(e.target.value)); if (scanStatus) setScanStatus(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleScan(); } }}
                data-testid="pdv-barcode-input"
              />
              {/* No celular não há leitor e o Enter do teclado virava "próximo
                  campo" (foto do Pedro, 23/09/2026): botão pra confirmar sem
                  depender do teclado. Só aparece onde o ponteiro é o dedo. */}
              <button
                type="submit"
                aria-label="Adicionar pelo código"
                disabled={!podeBarcode || !scanCode}
                className="hidden pointer-coarse:flex absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-40"
                data-testid="pdv-barcode-go"
              >
                <CornerDownLeft className="w-4 h-4" />
              </button>
            </form>
            {scanStatus && (
              <p className={`text-xs -mt-1.5 ${scanStatus.ok ? "text-green-700 dark:text-green-400" : "text-destructive"}`} data-testid="pdv-barcode-status">{scanStatus.text}</p>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={cart.search}
                onChange={(e) => cart.setSearch(e.target.value)}
                placeholder="Buscar por nome ou código"
                className="pl-9"
                data-testid="pdv-search-input"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => cart.setActiveCategory(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cart.activeCategory === null ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
                data-testid="pdv-category-all"
              >
                Todos
              </button>
              {(categories as Category[]).map((c) => (
                <button
                  key={c.id}
                  onClick={() => cart.setActiveCategory(c.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cart.activeCategory === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
                  data-testid={`pdv-category-${c.id}`}
                >
                  {sentenceCasePtBR(c.name)}
                </button>
              ))}
            </div>
          </div>

          <div className="md:flex-1 md:overflow-y-auto p-4 space-y-2">
            {cart.filteredProducts.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum produto encontrado</p>
              </div>
            ) : (
              cart.filteredProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onClick={() => { cart.setDetailProduct(p); cart.setEditingItem(null); }}
                  qtyInCart={cart.getCartItemQty(p.id)}
                  onQuickAdd={() => cart.handleQuickAdd(p)}
                  onQuickRemove={() => cart.handleQuickRemove(p)}
                  onQuickIncrement={() => cart.handleQuickIncrement(p)}
                />
              ))
            )}
          </div>
        </div>

        <div className={`md:w-96 lg:w-[28rem] border-l border-border flex flex-col bg-card overflow-hidden ${cart.showCartMobile ? "flex flex-1" : "hidden md:flex"}`}>
          <div className="p-4 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <span className="font-semibold">Pedido ({cart.cart.length})</span>
              </div>
              <div className="flex gap-2">
                <button onClick={cart.handleClearCart} className="text-xs text-muted-foreground hover:text-destructive" disabled={cart.cart.length === 0} data-testid="pdv-clear-cart">
                  Limpar
                </button>
                <button onClick={() => cart.setShowCartMobile(false)} className="md:hidden p-1 hover:bg-muted rounded" data-testid="pdv-close-cart-mobile">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {cart.editingOrder && (
              <div className="flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded px-2.5 py-1.5">
                <span className="flex items-center gap-1.5 min-w-0 truncate">
                  <Pencil className="w-3.5 h-3.5 shrink-0" />
                  Editando pedido
                  <span className="font-mono font-semibold">#{editingCode}</span>
                  {cart.editingOrder.source === "link" && <span className="font-semibold">· do link</span>}
                  {cart.tableNumber.trim() && (
                    <span className="font-semibold">· {rotuloConta(cart.tableNumber.trim(), (store as any)?.tableQrPerClient === true)}</span>
                  )}
                </span>
                <button onClick={cart.cancelEdit} className="underline shrink-0" data-testid="pdv-cancel-edit">
                  cancelar
                </button>
              </div>
            )}
            {comandaDoPedido && (
              <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-3 py-2.5" data-testid="pdv-comanda-nova">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">Pedido novo na comanda {comandaDoPedido.mesa}</span>
                  <span className="block text-xs">Conta de {comandaDoPedido.nome}. Escolha os produtos e finalize.</span>
                </span>
                <button onClick={cart.handleClearCart} className="text-xs underline shrink-0" data-testid="pdv-comanda-nova-cancelar">
                  cancelar
                </button>
              </div>
            )}
            {/* <md: no desktop a grade está do lado; no celular o carrinho
                cobre a tela e não havia caminho visível pra adicionar item. */}
            {cart.editingOrder && (
              <button
                type="button"
                onClick={() => cart.setShowCartMobile(false)}
                className="md:hidden w-full flex items-center justify-center gap-1.5 text-sm font-medium text-primary border border-dashed border-primary/60 rounded-lg py-2 hover:bg-primary/5"
                data-testid="pdv-edit-add-products"
              >
                <Plus className="w-4 h-4" />
                Adicionar produtos
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cart.cart.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">
                Adicione produtos para começar
              </div>
            ) : (
              cart.cart.map((it) => (
                <CartItemRow
                  key={it.cartItemId}
                  item={it}
                  onRemove={() => cart.handleRemoveItem(it.cartItemId)}
                  onQtyChange={(q) => cart.handleQtyChange(it.cartItemId, q)}
                  onEdit={() => {
                    if (it.ghost) {
                      toast({ title: "Item de pedido antigo", description: "Este produto saiu do catálogo — remova e adicione de novo para alterar.", variant: "destructive" });
                      return;
                    }
                    cart.setDetailProduct(it.product); cart.setEditingItem(it);
                  }}
                />
              ))
            )}

            {cart.cart.length > 0 && (
              <>
                {cart.dataUnica && cart.cart.some((it) => (it.product as any)?.isOrderBased) && (
                  <EncomendaDoCarrinhoPDV
                    slug={slug}
                    itens={itensDeEncomenda(cart.cart)}
                    data={cart.encData}
                    hora={cart.encHora}
                    turno={cart.encTurno}
                    horarios={cart.horariosDosTurnos}
                    diasDoPedido={cart.editingOrder?.diasAgendados}
                    onChange={cart.setEncomenda}
                  />
                )}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs">Observações do pedido</Label>
                  <textarea
                    value={cart.orderNotes}
                    onChange={(e) => cart.setOrderNotes(e.target.value)}
                    rows={2}
                    placeholder="Ex: caprichar no embrulho..."
                    className="w-full text-sm px-3 py-2 border border-border rounded bg-background"
                    data-testid="pdv-order-notes"
                  />
                </div>

                <MesaSection
                  tableNumber={cart.tableNumber}
                  onTableNumberChange={cart.setTableNumber}
                  payLater={cart.payLater}
                  onPayLaterChange={cart.setPayLater}
                />

                <CustomerSection
                  customer={cart.customer}
                  onChange={cart.setCustomer}
                  customerRequired={cart.customerRequired}
                  useCustomerSearch={useCustomerSearch}
                  avisoSemWhatsApp={cart.pontosNoPdv ? AVISO_PONTOS_SEM_WHATSAPP : undefined}
                />

                {(cart.allowsDelivery || !!cart.editingOrder?.eraCombinar) && (
                  <>
                    <DeliverySection
                      deliveryMode={cart.deliveryMode}
                      onDeliveryModeChange={cart.setDeliveryMode}
                      address={cart.address}
                      onAddressChange={cart.setAddressManual}
                      store={store}
                      feeInfo={cart.feeInfo}
                      finalDeliveryFee={cart.deliveryFee}
                      freeDeliveryApplied={cart.freeDeliveryApplied}
                      allowsPickup={true}
                      allowsDelivery={cart.allowsDelivery}
                      podeCombinar={!!cart.editingOrder?.eraCombinar}
                      feeOverride={cart.feeOverride}
                      onFeeOverrideChange={cart.setFeeOverride}
                    />
                    {cart.deliveryMode === "entrega" && cart.customer.kind === "existing" && (
                      <SavedAddressesPicker
                        customerId={cart.customer.customer.id}
                        savedAddressId={cart.savedAddressId}
                        onPick={cart.pickSavedAddress}
                      />
                    )}
                  </>
                )}

                <DiscountSection
                  discountValue={cart.discountValue}
                  discountType={cart.discountType}
                  onValueChange={cart.setDiscountValue}
                  onTypeChange={cart.setDiscountType}
                />

                {cart.programaPdv && (
                  <PontosDoPedido
                    titulo="Pontos do cliente"
                    programa={cart.programaPdv}
                    verificado
                    saldo={cart.saldoPdv}
                    escolhidos={cart.premiosPdv}
                    onEscolher={cart.setPremiosPdv}
                    problemaDe={(r) => cart.problemaPdv(r)}
                    rendimento={cart.rendimentoPdv}
                    descontoDe={(id) => cart.rewardUsesPdv.find((u) => u.rewardId === id)?.discount ?? 0}
                    pontosUsadosDe={(id) => cart.rewardUsesPdv.find((u) => u.rewardId === id)?.points ?? 0}
            onConfirmar={() => {}}
                    aviso={
                      cart.customer.kind === "existing" && !temWhatsApp(cart.customer.customer.whatsappNumber) ? (
                        <>
                          {AVISO_PONTOS_SEM_WHATSAPP}{" "}
                          <button type="button" className="font-semibold underline" onClick={() => setInformandoWhatsApp(true)} data-testid="pdv-informar-whatsapp">
                            Informar WhatsApp
                          </button>
                        </>
                      ) : undefined
                    }
                  />
                )}
                {informandoWhatsApp && cart.customer.kind === "existing" && (
                  <EditCustomerModal
                    customer={cart.customer.customer}
                    onClose={() => setInformandoWhatsApp(false)}
                    onSaved={(c) => cart.setCustomer({ kind: "existing", customer: c })}
                  />
                )}

                {cart.payLater ? (
                  <div className="text-xs bg-muted/60 border border-border rounded px-3 py-2 text-muted-foreground">
                    Pagamento <span className="font-semibold">Pendente</span> — a forma será escolhida ao fechar a conta.
                  </div>
                ) : (
                  <PaymentSection
                    acceptedPayments={cart.acceptedPayments}
                    paymentMethod={cart.paymentMethod}
                    onPaymentMethodChange={cart.setPaymentMethod}
                    paymentMixed={cart.paymentMixed}
                    onPaymentMixedChange={cart.setPaymentMixed}
                    paymentParts={cart.paymentParts}
                    onPaymentPartsChange={cart.setPaymentParts}
                    cashReceived={cart.cashReceived}
                    onCashReceivedChange={cart.setCashReceived}
                    total={cart.total}
                    cashChange={cart.cashChange}
                    crediarioNames={cart.crediarioNames}
                    crediarioInstallments={cart.crediarioInstallments}
                    onCrediarioInstallmentsChange={cart.setCrediarioInstallments}
                    crediarioFirstDue={cart.crediarioFirstDue}
                    onCrediarioFirstDueChange={cart.setCrediarioFirstDue}
                    crediarioInterest={cart.crediarioInterest}
                    crediarioTravado={cart.crediarioTravado}
                  />
                )}

                <SuggestiveSection
                  products={products as Product[]}
                  cart={cart.cart}
                  store={store}
                  onPick={cart.handleQuickAdd}
                />
              </>
            )}
          </div>

          {cart.cart.length > 0 && (
            <div className="border-t border-border p-4 space-y-2 bg-card">
              <MinOrderBanner minOrderValue={cart.minOrderValue} subtotal={cart.subtotal} />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(cart.subtotal + quantityPromoDiscounts(cart.cart, "pdv").reduce((s, e) => s + e.discount, 0))}</span>
              </div>
              {quantityPromoDiscounts(cart.cart, "pdv").map((e) => (
                <div key={e.productId} className="flex justify-between text-sm text-green-600">
                  <span>Promoção {e.productName}</span>
                  <span>- {formatCurrency(e.discount)}</span>
                </div>
              ))}
              {cart.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span>
                  <span>- {formatCurrency(cart.discountAmount)}</span>
                </div>
              )}
              {cart.rewardUsesPdv.filter((u) => u.discount > 0).map((u) => (
                <div key={u.rewardId} className="flex justify-between text-sm text-green-600" data-testid="pdv-pontos-linha">
                  <span>Pontos ({u.name})</span>
                  <span>- {formatCurrency(u.discount)}</span>
                </div>
              ))}
              {cart.surcharge > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>Acréscimo cartão</span>
                  <span>+ {formatCurrency(cart.surcharge)}</span>
                </div>
              )}
              {cart.taxaServico > 0 && (
                <div className="flex justify-between text-sm" data-testid="pdv-cart-taxa-servico">
                  <span className="text-muted-foreground">Taxa de serviço ({String(cart.taxaServicoPct).replace(".", ",")}%)</span>
                  <span>+ {formatCurrency(cart.taxaServico)}</span>
                </div>
              )}
              {cart.deliveryMode === "entrega" && cart.deliveryFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Entrega</span>
                  <span>+ {formatCurrency(cart.deliveryFee)}</span>
                </div>
              )}
              {cart.deliveryMode === "entrega" && cart.freeDeliveryApplied && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Entrega</span>
                  <span>Grátis</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                <span>Total</span>
                <span data-testid="pdv-total">{formatCurrency(cart.total)}</span>
              </div>
              {cart.cashChange > 0 && (
                <div className="flex justify-between text-sm font-semibold text-blue-600">
                  <span>Troco</span>
                  <span>{formatCurrency(cart.cashChange)}</span>
                </div>
              )}
              {/* Finalizar inline — só em desktop. Em mobile vira a cart bar
                  fixa no rodapé (vê fora deste container). */}
              {fechandoConta ? (
                <div className="hidden md:flex flex-col gap-2 mt-2">
                  <Button
                    onClick={() => { void fecharConta(true); }}
                    disabled={finalizeDisabled}
                    className="w-full"
                    size="lg"
                    data-testid="pdv-close-deliver"
                  >
                    {acaoFechar === "entregar" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Pago e marcar como entregue
                  </Button>
                  <Button
                    onClick={() => { void fecharConta(false); }}
                    disabled={finalizeDisabled}
                    variant="outline"
                    className="w-full"
                    data-testid="pdv-finalize-button"
                  >
                    {acaoFechar === "pagar" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Só registrar pagamento
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => { void finalizar(); }}
                  disabled={finalizeDisabled}
                  className="hidden md:flex w-full mt-2"
                  size="lg"
                  data-testid="pdv-finalize-button"
                >
                  {cart.submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registrando...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {finalizeLabel}</>}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cart bar fixa full-width mobile-only. Substitui o FAB pill antigo
          (pill é padrão do storefront onde catálogo é o herói; PDV é
          ferramenta de balcão e o total precisa estar sempre visível,
          target largo estilo iFood). Conteúdo muda conforme a tab:
            - tab Produtos → "Ver pedido › N · R$ X" (CTA pra abrir tab)
            - tab Pedido   → "Finalizar pedido · R$ X" (chama handleFinalize) */}
      {cart.cart.length > 0 && (
        barraDupla ? (
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 shadow-lg" data-testid="pdv-cart-bar-close">
            <button
              onClick={() => { void fecharConta(true); }}
              disabled={finalizeDisabled}
              className="w-full bg-primary text-primary-foreground px-4 py-3.5 flex items-center justify-between gap-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pdv-cart-bar-close-deliver"
            >
              <span className="flex items-center gap-2 text-left">
                {acaoFechar === "entregar" ? <Loader2 className="w-5 h-5 shrink-0 animate-spin" /> : <CheckCircle2 className="w-5 h-5 shrink-0" />}
                Pago e marcar como entregue
              </span>
              <span className="shrink-0">{formatCurrency(cart.total)}</span>
            </button>
            <button
              onClick={() => { void fecharConta(false); }}
              disabled={finalizeDisabled}
              className="w-full bg-background text-primary border-t border-border px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pdv-cart-bar-finalize"
            >
              {acaoFechar === "pagar" && <Loader2 className="w-4 h-4 animate-spin" />}
              Só registrar pagamento
            </button>
          </div>
        ) : tabPedidoAtiva ? (
          <button
            onClick={() => { void finalizar(); }}
            disabled={finalizeDisabled}
            className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-primary text-primary-foreground px-4 py-4 flex items-center justify-between font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pdv-cart-bar-finalize"
          >
            <span className="flex items-center gap-2">
              {cart.submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              {cart.submitting ? "Registrando…" : finalizeLabel}
            </span>
            <span>{formatCurrency(cart.total)}</span>
          </button>
        ) : (
          <button
            onClick={() => cart.setShowCartMobile(true)}
            className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-primary text-primary-foreground px-4 py-4 flex items-center justify-between font-semibold shadow-lg"
            data-testid="pdv-cart-bar-ver-pedido"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              {cart.editingOrder ? <>Voltar ao pedido <span className="font-mono">#{editingCode}</span></> : <>Ver pedido ({cart.cart.length})</>}
            </span>
            <span>{formatCurrency(cart.total)}</span>
          </button>
        )
      )}

      {cart.detailProduct && (
        <ProductDetailDialog
          product={cart.detailProduct}
          editingItem={cart.editingItem}
          onClose={() => { cart.setDetailProduct(null); cart.setEditingItem(null); }}
          onConfirm={cart.handleAddOrUpdateItem}
          remainingKg={cart.remainingKg(cart.detailProduct, cart.editingItem?.cartItemId)}
          slug={slug}
          dataUnica={cart.dataUnica}
          horarios={cart.horariosDosTurnos}
          diasDoPedido={cart.editingOrder?.diasAgendados}
          canalDaPromo={cart.canalDaPromo}
        />
      )}

      {cart.notifyPrompt && (
        <NotifyCustomerDialog
          message={cart.notifyPrompt.message}
          onMessageChange={(m) => cart.setNotifyPrompt({ ...cart.notifyPrompt!, message: m })}
          onSend={cart.sendNotify}
          onSkip={cart.dismissNotify}
        />
      )}
      {cart.success && !cart.notifyPrompt && (
        <SuccessDialog
          success={cart.success}
          onPrint={cart.handlePrint}
          onWhatsApp={cart.handleSendWhatsApp}
          onNewSale={cart.handleNewSale}
          printerConnected={cart.printer.connected}
        />
      )}
    </div>
  );
}
